/**
 * Billing Modal Handler
 * Ventana obligatoria de boleta/factura antes del checkout.
 *
 * Cambios 2026-09-02 (Props):
 *  1. Una sola instancia por página (antes se creaba una nueva en cada
 *     'cart-drawer:updated', y cada una registraba sus propios listeners:
 *     N ventanas duplicadas y N envíos al apretar Pagar).
 *  2. El RUT ya no se valida mientras se escribe; solo al salir del campo
 *     y al enviar. Antes mostraba "RUT inválido" tecla por tecla.
 *  3. Estilos de error inyectados desde este archivo (.field-error, input.error,
 *     .required-indicator, .modal-error). El tema no los define, por eso el
 *     mensaje salía gris y sin borde rojo. Se quitó el style inline de los spans.
 *  4. Bloqueo de doble envío en handleSubmit.
 *  5. z-index 60 en #billing-modal: el cajón del carro está en 40 y la
 *     ventana (30) quedaba detrás.
 *
 * Requiere billing-storage.js cargado antes.
 */
class BillingModal {
  constructor() {
    this.modal = null;
    this.form = null;
    this.pagarButton = null;
    this.invoiceFields = null;
    this.rutFields = null;
    this.submitting = false;
    this.init();
  }

  /**
   * Valida un RUT chileno (módulo 11). Acepta con o sin puntos y guión.
   */
  validateRut(rut) {
    let cleanedRut = rut.replace(/\./g, "").replace(/-/g, "").replace(/\s/g, "").toUpperCase();

    if (cleanedRut.length < 2) {
      return false;
    }

    let body = cleanedRut.slice(0, -1);
    let dv = cleanedRut.slice(-1);

    if (!/^[0-9]+$/.test(body)) {
      return false;
    }

    let sum = 0;
    let factor = 2;
    for (let i = body.length - 1; i >= 0; i--) {
      sum += parseInt(body.charAt(i), 10) * factor;
      factor = factor === 7 ? 2 : factor + 1;
    }

    let calculatedDv = 11 - (sum % 11);
    if (calculatedDv === 11) {
      calculatedDv = "0";
    } else if (calculatedDv === 10) {
      calculatedDv = "K";
    } else {
      calculatedDv = String(calculatedDv);
    }

    return String(calculatedDv) === dv;
  }

  /**
   * Formatea un RUT con puntos y guión.
   */
  formatRut(rut) {
    let cleanedRut = rut.replace(/\./g, "").replace(/-/g, "").replace(/\s/g, "").toUpperCase();

    if (cleanedRut.length < 2) {
      return rut;
    }

    let body = cleanedRut.slice(0, -1);
    let dv = cleanedRut.slice(-1);
    let formattedBody = body.replace(/\B(?=(\d{3})+(?!\d))/g, ".");

    return `${formattedBody}-${dv}`;
  }

  init() {
    this.injectStyles();
    this.bindEvents();
  }

  /**
   * Estilos de error. El tema Enterprise no define estas clases.
   * Colores tomados de la configuración del tema (very_low_stock_text_color, error_bg_color).
   */
  injectStyles() {
    if (document.getElementById('billing-modal-styles')) return;
    const style = document.createElement('style');
    style.id = 'billing-modal-styles';
    style.textContent = `
      /* El cajón del carro (.drawer) tiene z-index 40 y modal.css deja .modal en 30:
         la ventana quedaba detrás del cajón. */
      #billing-modal {
        z-index: 60;
      }
      #billing-modal .field-error {
        display: none;
        color: #b40c1c;
        font-size: 1rem;
        font-weight: 600;
        line-height: 1.4;
        margin-top: 0.375rem;
      }
      #billing-modal .field-error.show {
        display: block;
      }
      #billing-modal input.error,
      #billing-modal textarea.error {
        border-color: #b40c1c;
        box-shadow: 0 0 0 1px #b40c1c;
      }
      #billing-modal .required-indicator {
        color: #b40c1c;
      }
      #billing-modal .modal-error {
        display: block;
        padding: 0.75rem 1rem;
        margin-bottom: 1rem;
        background: #fcedee;
        color: #b40c1c;
        border-radius: 4px;
      }
    `;
    document.head.appendChild(style);
  }

  bindEvents() {
    // Intercepta el botón Pagar del carro y del cajón
    document.addEventListener('click', (event) => {
      const checkoutButton = event.target.closest('button[name="checkout"]');
      if (checkoutButton && !event.target.closest('#billing-modal')) {
        event.preventDefault();
        this.showModal();
      }
    });

    // Cambio Boleta / Factura
    document.addEventListener('change', (event) => {
      if (event.target.name === 'attributes[factura_tipo]') {
        this.handleBillingTypeChange(event.target.value);
      }
    });

    // Formateo del RUT al escribir (sin validar todavía) y persistencia
    document.addEventListener('input', (event) => {
      if (event.target.name === 'attributes[factura_rut]') {
        this.handleRutInput(event.target);
      }

      if (event.target.closest('#billing-modal') && event.target.name && event.target.name.startsWith('attributes[factura_')) {
        this.persistField(event.target);
      }
    });

    // Validación al salir del campo
    document.addEventListener('blur', (event) => {
      if (event.target.closest('#billing-modal') && (event.target.tagName === 'INPUT' || event.target.tagName === 'TEXTAREA')) {
        this.validateField(event.target);

        if (event.target.name && event.target.name.startsWith('attributes[factura_')) {
          this.persistField(event.target);
        }
      }
    }, true);

    // Envío del formulario
    document.addEventListener('submit', (event) => {
      if (event.target.id === 'billing-modal-form') {
        event.preventDefault();

        if (this.validateForm()) {
          this.handleSubmit();
        } else {
          const firstError = this.modal.querySelector('.field-error.show');
          if (firstError) {
            firstError.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        }
      }
    });

    // Cerrar
    document.addEventListener('click', (event) => {
      if (event.target.classList.contains('js-close-modal') || event.target.closest('.js-close-modal')) {
        this.hideModal();
      }
    });

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && this.modal && this.modal.hasAttribute('open')) {
        this.hideModal();
      }
    });
  }

  showModal() {
    if (!this.modal) {
      this.createModal();
    }

    const selectedType = this.form.querySelector('input[name="attributes[factura_tipo]"]:checked');
    if (selectedType) {
      this.handleBillingTypeChange(selectedType.value);
    }

    this.prefillFromStorage();

    this.modal.setAttribute('open', '');
    document.body.style.overflow = 'hidden';

    const firstInput = this.modal.querySelector('input[type="radio"]:checked');
    if (firstInput) {
      firstInput.focus();
    }
  }

  hideModal() {
    if (this.modal) {
      this.modal.removeAttribute('open');
      document.body.style.overflow = '';
    }
  }

  handleRutInput(field) {
    const cursorPosition = field.selectionStart;
    const value = field.value;

    if (value.length > 0) {
      const cleanValue = value.replace(/\./g, "").replace(/-/g, "").replace(/\s/g, "");

      if (cleanValue.length >= 2) {
        const formattedValue = this.formatRut(cleanValue);

        if (formattedValue !== value) {
          field.value = formattedValue;
          const newCursorPos = Math.min(cursorPosition + (formattedValue.length - value.length), formattedValue.length);
          field.setSelectionRange(newCursorPos, newCursorPos);
        }
      }
    }
  }

  createModal() {
    const modalHTML = `
      <modal-dialog class="modal fixed top-0 left-0 w-full h-full flex items-center justify-center" id="billing-modal" aria-label="Información de Facturación" aria-modal="true" tabindex="-1">
        <div class="modal__window relative bg-theme-bg text-theme-text text-start overflow-hidden has-motion" role="dialog" aria-label="Información de Facturación" aria-modal="true" tabindex="-1">
          <button type="button" class="modal__close-btn absolute js-close-modal">
            <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M17 1L1 17M1 1L17 17" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
            <span class="visually-hidden">Cerrar</span>
          </button>

          <div class="modal__content flex flex-col h-full">
            <div class="billing-modal-content flex-1 overflow-y-auto">
              <h2 class="h2 mb-6">Información de Facturación</h2>

              <form id="billing-modal-form" class="billing-form" novalidate>
                <div class="billing-type-selector mb-6">
                  <fieldset class="billing-type-fieldset">
                    <legend class="h5 mb-4">Seleccionar tipo de documento</legend>

                    <div class="billing-option mb-3">
                      <input
                        type="radio"
                        id="modal-billing-ticket"
                        name="attributes[factura_tipo]"
                        value="boleta"
                        class="radio"
                        checked
                      >
                      <label for="modal-billing-ticket" class="regular-text ml-2">
                        Boleta
                      </label>
                    </div>

                    <div class="billing-option mb-3">
                      <input
                        type="radio"
                        id="modal-billing-invoice"
                        name="attributes[factura_tipo]"
                        value="factura"
                        class="radio"
                      >
                      <label for="modal-billing-invoice" class="regular-text ml-2">
                        Factura
                      </label>
                    </div>
                  </fieldset>
                </div>

                <div class="rut-fields mb-6" id="modal-rut-fields">
                  <div>
                    <label class="regular-text block mb-2" for="modal-tax-id-boleta">
                      Rut
                      <span class="required-indicator">*</span>
                    </label>
                    <input
                      type="text"
                      id="modal-tax-id-boleta"
                      name="attributes[factura_rut]"
                      class="input w-full"
                      inputmode="text"
                      autocomplete="off"
                      placeholder="12.345.678-9"
                    >
                    <span class="field-error" id="modal-tax-id-boleta-error" role="alert"></span>
                  </div>
                </div>

                <div class="invoice-fields" id="modal-invoice-fields" style="display: none;">
                  <div class="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-6">
                    <div>
                      <label class="regular-text block mb-2" for="modal-business-name">
                        Razón Social
                        <span class="required-indicator">*</span>
                      </label>
                      <input
                        type="text"
                        id="modal-business-name"
                        name="attributes[factura_razon_social]"
                        class="input w-full"
                        autocomplete="organization"
                        placeholder=""
                      >
                      <span class="field-error" id="modal-business-name-error" role="alert"></span>
                    </div>

                    <div>
                      <label class="regular-text block mb-2" for="modal-tax-id">
                        Rut
                        <span class="required-indicator">*</span>
                      </label>
                      <input
                        type="text"
                        id="modal-tax-id"
                        name="attributes[factura_rut]"
                        class="input w-full"
                        inputmode="text"
                        autocomplete="off"
                        placeholder="76.123.456-7"
                      >
                      <span class="field-error" id="modal-tax-id-error" role="alert"></span>
                    </div>

                    <div>
                      <label class="regular-text block mb-2" for="modal-company-name">
                        Giro
                        <span class="required-indicator">*</span>
                      </label>
                      <input
                        type="text"
                        id="modal-company-name"
                        name="attributes[factura_giro]"
                        class="input w-full"
                        placeholder=""
                      >
                      <span class="field-error" id="modal-company-name-error" role="alert"></span>
                    </div>

                    <div class="sm:col-span-2">
                      <label class="regular-text block mb-2" for="modal-business-address">
                        Dirección comercial
                        <span class="required-indicator">*</span>
                      </label>
                      <textarea
                        id="modal-business-address"
                        name="attributes[factura_direccion_comercial]"
                        class="input w-full"
                        rows="2"
                        autocomplete="street-address"
                        placeholder=""
                      ></textarea>
                      <span class="field-error" id="modal-business-address-error" role="alert"></span>
                    </div>

                    <div>
                      <label class="regular-text block mb-2" for="modal-comuna">
                        Comuna
                        <span class="required-indicator">*</span>
                      </label>
                      <input
                        type="text"
                        id="modal-comuna"
                        name="attributes[factura_comuna]"
                        class="input w-full"
                        placeholder=""
                      >
                      <span class="field-error" id="modal-comuna-error" role="alert"></span>
                    </div>

                    <div>
                      <label class="regular-text block mb-2" for="modal-ciudad">
                        Ciudad
                        <span class="required-indicator">*</span>
                      </label>
                      <input
                        type="text"
                        id="modal-ciudad"
                        name="attributes[factura_ciudad]"
                        class="input w-full"
                        autocomplete="address-level2"
                        placeholder=""
                      >
                      <span class="field-error" id="modal-ciudad-error" role="alert"></span>
                    </div>

                    <div class="mb-2">
                      <label class="regular-text block mb-2" for="modal-business-phone">
                        Teléfono
                        <span class="required-indicator">*</span>
                      </label>
                      <input
                        type="tel"
                        id="modal-business-phone"
                        name="attributes[factura_telefono]"
                        class="input w-full"
                        autocomplete="tel"
                        placeholder=""
                      >
                      <span class="field-error" id="modal-business-phone-error" role="alert"></span>
                    </div>

                    <div class="mb-2">
                      <label class="regular-text block mb-2" for="modal-business-email">
                        Email de factura
                        <span class="required-indicator">*</span>
                      </label>
                      <input
                        type="email"
                        id="modal-business-email"
                        name="attributes[factura_email]"
                        class="input w-full"
                        autocomplete="email"
                        placeholder=""
                      >
                      <span class="field-error" id="modal-business-email-error" role="alert"></span>
                    </div>
                  </div>
                </div>
              </form>
            </div>

            <div class="modal-actions sticky bottom-0 bg-theme-bg border-t border-gray-200 p-4">
              <div class="flex justify-center">
                <button type="submit" class="btn btn--primary" id="modal-pagar-btn" form="billing-modal-form">
                  PAGAR
                </button>
              </div>
            </div>
          </div>
        </div>
      </modal-dialog>
    `;

    document.body.insertAdjacentHTML('beforeend', modalHTML);

    this.modal = document.getElementById('billing-modal');
    this.form = document.getElementById('billing-modal-form');
    this.pagarButton = document.getElementById('modal-pagar-btn');
    this.invoiceFields = document.getElementById('modal-invoice-fields');
    this.rutFields = document.getElementById('modal-rut-fields');
  }

  handleBillingTypeChange(billingType) {
    if (!this.rutFields || !this.invoiceFields) return;

    if (window.BillingStorage) {
      window.BillingStorage.save({ factura_tipo: billingType });
    }

    if (billingType === 'boleta') {
      this.rutFields.style.display = 'block';
      const boletaRutInput = this.rutFields.querySelector('input');
      if (boletaRutInput) {
        boletaRutInput.setAttribute('required', 'required');
        boletaRutInput.removeAttribute('disabled');
      }
      this.invoiceFields.style.display = 'none';
      const invoiceInputs = this.invoiceFields.querySelectorAll('input, textarea');
      invoiceInputs.forEach(input => {
        input.removeAttribute('required');
        input.setAttribute('disabled', 'disabled');
        this.clearFieldError(input);
      });
    } else {
      this.rutFields.style.display = 'none';
      const boletaRutInput = this.rutFields.querySelector('input');
      if (boletaRutInput) {
        boletaRutInput.removeAttribute('required');
        boletaRutInput.setAttribute('disabled', 'disabled');
        this.clearFieldError(boletaRutInput);
      }
      this.invoiceFields.style.display = 'block';
      const invoiceInputs = this.invoiceFields.querySelectorAll('input, textarea');
      invoiceInputs.forEach(input => {
        input.setAttribute('required', 'required');
        input.removeAttribute('disabled');
      });
    }
  }

  validateField(field) {
    const errorElement = document.getElementById(`${field.id}-error`);
    let isValid = true;
    let errorMessage = '';

    this.clearFieldError(field);

    if (field.hasAttribute('required') && !field.value.trim()) {
      isValid = false;
      errorMessage = 'Este campo es obligatorio';
    }

    if (field.type === 'email' && field.value.trim()) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(field.value)) {
        isValid = false;
        errorMessage = 'Por favor, introduce un email válido';
      }
    }

    if (field.type === 'tel' && field.value.trim()) {
      const phoneRegex = /^[\+]?[0-9\s\-\(\)]{9,}$/;
      if (!phoneRegex.test(field.value)) {
        isValid = false;
        errorMessage = 'Por favor, introduce un número de teléfono válido';
      }
    }

    if (field.name === 'attributes[factura_rut]' && field.value.trim()) {
      if (!this.validateRut(field.value.trim())) {
        isValid = false;
        errorMessage = 'El RUT no es válido. Revisa el dígito verificador (ej: 12.345.678-9)';
      }
    }

    if (!isValid && errorElement) {
      errorElement.textContent = errorMessage;
      errorElement.classList.add('show');
      field.classList.add('error');
      field.setAttribute('aria-invalid', 'true');
    }

    return isValid;
  }

  clearFieldError(field) {
    const errorElement = document.getElementById(`${field.id}-error`);
    if (errorElement) {
      errorElement.textContent = '';
      errorElement.classList.remove('show');
    }
    field.classList.remove('error');
    field.removeAttribute('aria-invalid');
  }

  validateForm() {
    const billingType = this.form.querySelector('input[name="attributes[factura_tipo]"]:checked');
    let isValid = true;

    const allFields = this.form.querySelectorAll('input, textarea');
    allFields.forEach(field => {
      this.clearFieldError(field);
    });

    if (billingType && billingType.value === 'boleta') {
      const rutField = this.rutFields.querySelector('input[required]');
      if (rutField && !this.validateField(rutField)) {
        isValid = false;
      }
    } else if (billingType && billingType.value === 'factura') {
      const requiredFields = this.invoiceFields.querySelectorAll('input[required], textarea[required]');
      requiredFields.forEach(field => {
        if (!this.validateField(field)) {
          isValid = false;
        }
      });
    }

    return isValid;
  }

  async handleSubmit() {
    if (this.submitting) return;
    this.submitting = true;

    const formData = new FormData(this.form);
    const billingAttributes = {};
    const billingType = this.form.querySelector('input[name="attributes[factura_tipo]"]:checked');

    for (let [key, value] of formData.entries()) {
      if (key.startsWith('attributes[')) {
        const attrKey = key.replace('attributes[', '').replace(']', '');

        if (attrKey === 'factura_tipo') {
          billingAttributes[attrKey] = value;
        } else if (attrKey === 'factura_rut') {
          billingAttributes[attrKey] = value;
        } else if (billingType && billingType.value === 'factura' && attrKey.startsWith('factura_')) {
          billingAttributes[attrKey] = value;
        }
      }
    }

    const cartNoteDrawer = document.querySelector('#cart-drawer-form #cart-note');
    const cartNoteMain = document.querySelector('#cart #cart-note');
    let cartNote = '';

    if (cartNoteDrawer && cartNoteDrawer.value.trim()) {
      cartNote = cartNoteDrawer.value.trim();
    } else if (cartNoteMain && cartNoteMain.value.trim()) {
      cartNote = cartNoteMain.value.trim();
    }

    if (window.BillingStorage) {
      window.BillingStorage.save(billingAttributes);
    }

    this.pagarButton.disabled = true;
    this.pagarButton.textContent = 'Procesando...';

    try {
      const cartResponse = await fetch('/cart.js');
      if (!cartResponse.ok) {
        throw new Error('Error al obtener el estado del carrito');
      }

      const currentCart = await cartResponse.json();

      const mergedAttributes = {
        ...currentCart.attributes,
        ...billingAttributes
      };

      const response = await fetch('/cart/update.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          attributes: mergedAttributes,
          note: cartNote
        })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.description || 'Error al actualizar el carrito');
      }

      await response.json();

      window.location.href = '/checkout';

    } catch (error) {
      const errorMessage = error.message || 'Error al procesar la información. Por favor, inténtalo de nuevo.';

      let errorDisplay = this.modal.querySelector('.modal-error');
      if (!errorDisplay) {
        errorDisplay = document.createElement('div');
        errorDisplay.className = 'modal-error';
        errorDisplay.setAttribute('role', 'alert');
        this.form.insertBefore(errorDisplay, this.form.firstChild);
      }
      errorDisplay.textContent = errorMessage;

      this.pagarButton.disabled = false;
      this.pagarButton.textContent = 'PAGAR';
      this.submitting = false;
    }
  }

  persistField(field) {
    if (!window.BillingStorage) return;

    const key = field.name.replace('attributes[', '').replace(']', '');
    window.BillingStorage.save({ [key]: field.value.trim() });
  }

  prefillFromStorage() {
    if (!window.BillingStorage || !this.form) return;

    const data = window.BillingStorage.load();
    if (!data) return;

    const allRutInputs = this.form.querySelectorAll('input[name="attributes[factura_rut]"]');
    allRutInputs.forEach((input) => {
      if (data.factura_rut) {
        input.value = data.factura_rut;
      }
    });

    [
      'factura_razon_social', 'factura_giro',
      'factura_comuna', 'factura_ciudad', 'factura_direccion_comercial',
      'factura_telefono', 'factura_email'
    ].forEach(k => {
      const input = this.form.querySelector(
        `input[name="attributes[${k}]"], textarea[name="attributes[${k}]"]`
      );
      if (input && data[k]) {
        input.value = data[k];
      }
    });

    if (data.factura_tipo) {
      const typeInput = this.form.querySelector(
        `input[name="attributes[factura_tipo]"][value="${data.factura_tipo}"]`
      );
      if (typeInput) {
        typeInput.checked = true;
        this.handleBillingTypeChange(data.factura_tipo);
      }
    }
  }
}

/**
 * Cart Note Auto-Save Handler
 * Guarda la nota del pedido con debounce, al salir del campo y al cambiar.
 */
class CartNoteAutoSave {
  constructor() {
    this.debounceTimer = null;
    this.debounceDelay = 2000;
    this.lastSavedValue = '';
    this.init();
  }

  init() {
    this.bindEvents();
    this.setInitialValues();
  }

  setInitialValues() {
    const cartNoteDrawer = document.querySelector('#cart-drawer-form #cart-note');
    const cartNoteMain = document.querySelector('#cart #cart-note');

    if (cartNoteDrawer) {
      this.lastSavedValue = cartNoteDrawer.value;
    } else if (cartNoteMain) {
      this.lastSavedValue = cartNoteMain.value;
    }
  }

  bindEvents() {
    document.addEventListener('input', (event) => {
      if (event.target.id === 'cart-note') {
        this.handleNoteInput(event.target);
      }
    });

    document.addEventListener('blur', (event) => {
      if (event.target.id === 'cart-note') {
        this.handleNoteBlur(event.target);
      }
    }, true);

    document.addEventListener('change', (event) => {
      if (event.target.id === 'cart-note') {
        this.handleNoteChange(event.target);
      }
    });

    // Cuando el cajón se re-renderiza, releer el valor actual de la nota
    document.addEventListener('cart-drawer:updated', () => {
      this.setInitialValues();
    });
  }

  handleNoteInput(noteField) {
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
    }

    this.debounceTimer = setTimeout(() => {
      this.saveNoteIfChanged(noteField);
    }, this.debounceDelay);
  }

  handleNoteBlur(noteField) {
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
      this.debounceTimer = null;
    }
    this.saveNoteIfChanged(noteField);
  }

  handleNoteChange(noteField) {
    this.saveNoteIfChanged(noteField);
  }

  saveNoteIfChanged(noteField) {
    const currentValue = noteField.value.trim();

    if (currentValue !== this.lastSavedValue) {
      this.saveNote(currentValue);
      this.lastSavedValue = currentValue;
    }
  }

  async saveNote(noteValue) {
    try {
      const cartResponse = await fetch('/cart.js');
      if (!cartResponse.ok) {
        return;
      }

      const currentCart = await cartResponse.json();

      const response = await fetch('/cart/update.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          attributes: currentCart.attributes || {},
          note: noteValue
        })
      });

      if (!response.ok) {
        return;
      }
    } catch (error) {
      // Fallo silencioso a propósito
    }
  }
}

/**
 * Inicialización: una sola instancia de cada clase por página.
 * Los listeners van sobre document, así que no dependen de que el cajón
 * se vuelva a renderizar. Si el script se carga dos veces (carro + cajón),
 * el guard evita duplicados.
 */
(function () {
  const start = () => {
    if (!window.__milanBillingModal) {
      window.__milanBillingModal = new BillingModal();
    }
    if (!window.__milanCartNote) {
      window.__milanCartNote = new CartNoteAutoSave();
    }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();